import React, { useState, useRef } from "react";
import Header from "../../../Componets/Header";
import Loadercomponent from "../../../Componets/Loader";
import toast from "react-hot-toast";
import { Button } from "antd";
import {
    FilePdfOutlined,
    SwapOutlined,
    DoubleLeftOutlined,
    LeftOutlined,
    RightOutlined,
    DoubleRightOutlined,
} from "@ant-design/icons";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import formatDateDDMMYYYY from "../../../Main/formatDate";
import { get_trading_partner_report } from "../../../Controllers/Dashboard/Dashboard";

// ── Style tokens (monochrome, professional) ──────────────────────────────────
const S = {
    ink: "#111827",
    inkMid: "#374151",
    inkLight: "#6b7280",
    inkFaint: "#9ca3af",
    border: "#e5e7eb",
    borderMid: "#d1d5db",
    bg: "#ffffff",
    bgSubtle: "#f9fafb",
    bgRow: "#f3f4f6",
    green: "#059669",
    red: "#b91c1c",
    greenBg: "#f0fdf4",
    redBg: "#fef2f2",
    radius: 8,
    radiusSm: 4,
};

const cell = (extra = {}) => ({
    padding: "9px 12px",
    borderBottom: `1px solid ${S.border}`,
    fontSize: 12,
    color: S.inkMid,
    ...extra,
});

const thStyle = (align = "left") => ({
    padding: "9px 12px",
    textAlign: align,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: S.inkLight,
    borderBottom: `2px solid ${S.border}`,
    background: S.bg,
    whiteSpace: "nowrap",
});

// ── Format document number with prefix ──────────────────────────────────────
const formatNumber = (type, number) => {
    const prefixMap = {
        invoice: "INV",
        purchase: "PO",
        purchase_return: "PR",
        sales_return: "SR",
    };
    const prefix = prefixMap[type];
    return prefix ? `${prefix}-${number}` : `#${number}`;
};

const TypeLabel = ({ type }) => {
    const map = {
        invoice: "Sale",
        purchase: "Purchase",
        purchase_return: "Purchase Return",
        sales_return: "Sales Return",
    };
    return <span style={{ fontSize: 12, color: S.inkMid }}>{map[type] || type}</span>;
};

const DrCrBadge = ({ value }) => (
    <span style={{
        fontSize: 11,
        fontWeight: 700,
        color: value === "Dr" ? S.green : S.red,
        background: value === "Dr" ? S.greenBg : S.redBg,
        border: `1px solid ${value === "Dr" ? "#bbf7d0" : "#fecaca"}`,
        borderRadius: S.radiusSm,
        padding: "2px 7px",
        letterSpacing: "0.04em",
    }}>
        {value}
    </span>
);

const TradingPartnerReport = () => {
    const [loader, setLoader] = useState(false);
    const [ledger, setLedger] = useState([]);
    const [summary, setSummary] = useState({});
    const [pagination, setPagination] = useState({});
    const [filters, setFilters] = useState({});
    const componentRef = useRef();

    const headers = {
        name: "Trading Partner Ledger",
        components: [
            {
                type: "filter",
                name: "supplier_id",
                placeholder: "Select supplier (trading partner)",
                allowClear: true,
                showSearch: true,
                filterOption: false,
                api: "get-all-suppliers",
                value: null,
                options: null,
            },
            {
                type: "range",
                name: "date",
                placeholder: "Date range",
                value: { start: "", end: "" },
            },
        ],
        navigate: {
            tabs: [
                { name: "Reports", path: "/app/reports" },
                { name: "Trading Partner Ledger", path: "" },
            ],
        },
    };

    const [header, setHeader] = useState(headers);

    const fetchReport = async (f = {}, page = 1) => {
        try {
            setLoader(true);
            const response = await get_trading_partner_report({
                supplier_id: f.supplier_id || null,
                date: f.date || {},
                page,
                limit: 50,
            });
            if (response?.status) {
                setLedger(response.data.data || []);
                setSummary(response.data.summary || {});
                setPagination(response.data.pagination || {});
            } else {
                toast.error(response?.message || "Failed to fetch report");
            }
        } catch (e) {
            toast.error(e.message);
        } finally {
            setLoader(false);
        }
    };

    const formdata = (data) => {
        const f = { ...data };
        if (data.supplier_id && typeof data.supplier_id === "object") {
            f.supplier_id = data.supplier_id._id || data.supplier_id.id;
        }
        setFilters(f);
        fetchReport(f, 1);
    };

    const goTo = (p) => fetchReport(filters, p);
    const goFirst = () => pagination?.currentPage > 1 && goTo(1);
    const goLast = () => pagination?.currentPage < pagination?.totalPages && goTo(pagination.totalPages);
    const goPrev = () => pagination?.currentPage > 1 && goTo(pagination.currentPage - 1);
    const goNext = () => pagination?.currentPage < pagination?.totalPages && goTo(pagination.currentPage + 1);

    const pageNumbers = () => {
        const cur = pagination?.currentPage || 1;
        const tot = pagination?.totalPages || 1;
        if (tot <= 7) return Array.from({ length: tot }, (_, i) => i + 1);
        if (cur <= 4) return [1, 2, 3, 4, 5, "...", tot];
        if (cur >= tot - 3) return [1, "...", tot - 4, tot - 3, tot - 2, tot - 1, tot];
        return [1, "...", cur - 1, cur, cur + 1, "...", tot];
    };

    // ── CSV export (clean, no payment columns) ──────────────────────────────
    const downloadCSV = () => {
        if (!ledger.length) return toast.error("No data to export");
        const cols = ["#", "Date", "Number", "Type", "Debit (OMR)", "Credit (OMR)", "Balance (OMR)", "Dr/Cr"];
        const rows = ledger.map((tx, i) => [
            i + 1,
            formatDateDDMMYYYY(tx.date),
            `"${formatNumber(tx.type, tx.number)}"`,
            tx.description,
            tx.debit > 0 ? tx.debit.toFixed(3) : 0,
            tx.credit > 0 ? tx.credit.toFixed(3) : 0,
            tx.absBalance.toFixed(3),
            tx.drCr,
        ]);
        rows.push(["", "TOTALS", "", "",
            (summary.totalDebit || 0).toFixed(3),
            (summary.totalCredit || 0).toFixed(3),
            (summary.absPeriodNetBalance || 0).toFixed(3),
            (summary.totalDebit - summary.totalCredit) >= 0 ? "Dr" : "Cr",
        ]);
        const csv = [cols.join(","), ...rows.map(r => r.join(","))].join("\n");
        const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `TradingPartner_${summary.supplierName || "Report"}_${new Date().toISOString().split("T")[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(a.href);
    };

    // ── PDF export (clean, no payment columns) ──────────────────────────────
    const downloadPDF = () => {
        if (!ledger.length) return toast.error("No data to export");
        const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
        const pw = doc.internal.pageSize.getWidth();

        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.text("TRADING PARTNER LEDGER", pw / 2, 14, { align: "center" });

        doc.setLineWidth(0.2);
        doc.line(14, 18, pw - 14, 18);

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text(`Supplier: ${summary.supplierName || "-"}`, 14, 25);
        doc.text(`Customer: ${summary.customerName || "-"}`, 14, 30);

        // Add date range if present
        let dateRangeText = "";
        if (filters.date?.start && filters.date?.end) {
            dateRangeText = `${formatDateDDMMYYYY(filters.date.start)} – ${formatDateDDMMYYYY(filters.date.end)}`;
        } else if (filters.date?.start) {
            dateRangeText = `From ${formatDateDDMMYYYY(filters.date.start)}`;
        }
        doc.text(`Period: ${dateRangeText || "All transactions"}`, 14, 35);

        doc.text(`Generated: ${new Date().toLocaleString()}`, pw - 14, 25, { align: "right" });
        doc.setFont("helvetica", "bold");
        doc.text(
            `Net Balance: OMR ${(summary.absClosingBalance || 0).toFixed(3)} ${summary.netPosition}`,
            pw - 14, 30, { align: "right" }
        );

        // Build rows – use 0.000 instead of "—"
        const rows = ledger.map((tx, i) => [
            i + 1,
            formatDateDDMMYYYY(tx.date),
            formatNumber(tx.type, tx.number),
            tx.description,
            tx.debit > 0 ? tx.debit.toFixed(3) : "0.000",
            tx.credit > 0 ? tx.credit.toFixed(3) : "0.000",
            tx.absBalance.toFixed(3),
            tx.drCr,
        ]);
        rows.push(["", "TOTALS", "", "",
            (summary.totalDebit || 0).toFixed(3),
            (summary.totalCredit || 0).toFixed(3),
            (summary.absPeriodNetBalance || 0).toFixed(3),
            (summary.totalDebit - summary.totalCredit) >= 0 ? "Dr" : "Cr",
        ]);

        autoTable(doc, {
            startY: 42,
            head: [["#", "Date", "Number", "Type", "Debit", "Credit", "Balance", "Dr/Cr"]],
            body: rows,
            theme: "grid",
            headStyles: { fillColor: [243, 244, 246], textColor: [55, 65, 81], fontSize: 8, fontStyle: "bold" },
            bodyStyles: { fontSize: 8, textColor: [55, 65, 81] },
            didParseCell: (d) => {
                const last = rows.length - 1;
                if (d.row.index === last) {
                    d.cell.styles.fontStyle = "bold";
                    d.cell.styles.fillColor = [243, 244, 246];
                    d.cell.styles.textColor = [17, 24, 39];
                }
                if (d.section === "body" && d.column.index === 7) {
                    d.cell.styles.textColor =
                        d.cell.raw === "Dr" ? [5, 150, 105] :
                            d.cell.raw === "Cr" ? [185, 28, 28] : [55, 65, 81];
                }
            },
        });

        const blob = doc.output("blob");
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `TradingPartner_${summary.supplierName || "Ledger"}_${new Date().toISOString().split("T")[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const hasData = ledger.length > 0;
    const noData = !loader && ledger.length === 0;
    const netCr = summary.netPosition === "Cr";
    const netDr = summary.netPosition === "Dr";
    const periodNetSign = (summary.totalDebit || 0) - (summary.totalCredit || 0) >= 0 ? "Dr" : "Cr";

    // Show opening balance row only if a start date is selected
    const showOpeningRow = filters.date?.start && summary.openingBalance !== undefined;

    return (
        <Header headers={header} setHeaders={setHeader} formdata={formdata}>
            <Loadercomponent loader={loader}>
                <div style={{ display: "flex", flexDirection: "column", gap: 16, minHeight: "calc(100vh - 220px)" }}>

                    {/* Partner strip – shows closing balance (net position) */}
                    {(summary.supplierName || summary.customerName) && (
                        <div style={{
                            background: S.bgSubtle,
                            border: `1px solid ${S.border}`,
                            borderRadius: S.radius,
                            padding: "14px 20px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 12,
                        }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                                <div>
                                    <div style={{ fontSize: 10, color: S.inkFaint, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 2 }}>Supplier</div>
                                    <div style={{ fontSize: 15, fontWeight: 700, color: S.ink }}>{summary.supplierName}</div>
                                </div>
                                <SwapOutlined style={{ color: S.inkFaint, fontSize: 16 }} />
                                <div>
                                    <div style={{ fontSize: 10, color: S.inkFaint, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 2 }}>Customer</div>
                                    <div style={{ fontSize: 15, fontWeight: 700, color: S.ink }}>{summary.customerName}</div>
                                </div>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <div style={{ textAlign: "right" }}>
                                    <div style={{ fontSize: 10, color: S.inkFaint, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 2 }}>
                                        {netCr ? "You owe them" : netDr ? "They owe you" : "Fully settled"}
                                    </div>
                                    <div style={{ fontSize: 20, fontWeight: 800, color: netCr ? S.red : netDr ? S.green : S.inkMid }}>
                                        OMR {(summary.absClosingBalance || 0).toFixed(3)}
                                    </div>
                                </div>
                                <DrCrBadge value={summary.netPosition} />
                            </div>
                        </div>
                    )}

                    {/* Toolbar – transaction count and export buttons */}
                    {hasData && (
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div style={{ fontSize: 12, color: S.inkLight }}>
                                {pagination.totalCount || 0} transactions &nbsp;·&nbsp; Page {pagination.currentPage || 1} of {pagination.totalPages || 1}
                            </div>
                            <div style={{ display: "flex", gap: 8 }}>
                                <Button onClick={downloadCSV} style={{ fontSize: 12, fontWeight: 600 }}>
                                    Download CSV
                                </Button>
                                <Button onClick={downloadPDF} icon={<FilePdfOutlined />} style={{ fontSize: 12, fontWeight: 600 }}>
                                    Download PDF
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Ledger table – only essential columns */}
                    {hasData && (
                        <div style={{ background: S.bg, borderRadius: S.radius, border: `1px solid ${S.border}`, overflow: "hidden" }}>
                            <div style={{ overflowX: "auto", maxHeight: "52vh", overflowY: "auto" }} ref={componentRef}>
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                                    <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
                                        <tr>
                                            <th style={thStyle("center")}>#</th>
                                            <th style={thStyle()}>Date</th>
                                            <th style={thStyle()}>Number</th>
                                            <th style={thStyle()}>Type</th>
                                            <th style={thStyle("right")}>Debit (OMR)</th>
                                            <th style={thStyle("right")}>Credit (OMR)</th>
                                            <th style={thStyle("right")}>Run Balance</th>
                                            <th style={thStyle("center")}>Dr/Cr</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {/* Opening Balance Row */}
                                        {showOpeningRow && (
                                            <tr style={{ background: S.bgSubtle, fontWeight: 500 }}>
                                                <td style={cell({ textAlign: "center" })}>—</td>
                                                <td style={cell({ whiteSpace: "nowrap" })}>
                                                    {formatDateDDMMYYYY(filters.date.start)}
                                                </td>
                                                <td style={cell()}>Opening Balance</td>
                                                <td style={cell()}>—</td>
                                                <td style={cell({ textAlign: "right" })}>—</td>
                                                <td style={cell({ textAlign: "right" })}>—</td>
                                                <td style={cell({ textAlign: "right", fontWeight: 700, color: summary.openingDrCr === "Dr" ? S.green : S.red })}>
                                                    {summary.absOpeningBalance?.toFixed(3)}
                                                </td>
                                                <td style={cell({ textAlign: "center" })}>
                                                    <DrCrBadge value={summary.openingDrCr} />
                                                </td>
                                            </tr>
                                        )}

                                        {/* Transaction rows */}
                                        {ledger.map((tx, i) => {
                                            const even = i % 2 === 0;
                                            const rowBg = even ? S.bg : S.bgSubtle;
                                            return (
                                                <tr
                                                    key={i}
                                                    style={{ background: rowBg, transition: "background 0.1s" }}
                                                    onMouseEnter={(e) => e.currentTarget.style.background = "#f0f4f8"}
                                                    onMouseLeave={(e) => e.currentTarget.style.background = rowBg}
                                                >
                                                    <td style={cell({ textAlign: "center", color: S.inkFaint, fontSize: 11 })}>
                                                        {(pagination.currentPage - 1) * pagination.pageSize + i + 1}
                                                    </td>
                                                    <td style={cell({ whiteSpace: "nowrap" })}>
                                                        {formatDateDDMMYYYY(tx.date)}
                                                    </td>
                                                    <td style={cell({ fontFamily: "monospace", fontWeight: 600, color: S.ink })}>
                                                        {formatNumber(tx.type, tx.number)}
                                                    </td>
                                                    <td style={cell()}>
                                                        <TypeLabel type={tx.type} />
                                                    </td>
                                                    <td style={cell({ textAlign: "right", fontWeight: tx.debit > 0 ? 700 : 400, color: tx.debit > 0 ? S.green : S.inkFaint })}>
                                                        {tx.debit > 0 ? tx.debit.toFixed(3) : "0.000"}
                                                    </td>
                                                    <td style={cell({ textAlign: "right", fontWeight: tx.credit > 0 ? 700 : 400, color: tx.credit > 0 ? S.red : S.inkFaint })}>
                                                        {tx.credit > 0 ? tx.credit.toFixed(3) : "0.000"}
                                                    </td>
                                                    <td style={cell({ textAlign: "right", fontWeight: 700, color: tx.drCr === "Dr" ? S.green : S.red })}>
                                                        {tx.absBalance.toFixed(3)}
                                                    </td>
                                                    <td style={cell({ textAlign: "center" })}>
                                                        <DrCrBadge value={tx.drCr} />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>

                                    {/* Footer totals – period net */}
                                    <tfoot style={{ position: "sticky", bottom: 0, zIndex: 10 }}>
                                        <tr style={{ background: S.bgRow, borderTop: `2px solid ${S.borderMid}` }}>
                                            <td colSpan={4} style={{ padding: "9px 12px", fontSize: 11, fontWeight: 700, color: S.inkLight, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                                                Totals
                                            </td>
                                            <td style={{ padding: "9px 12px", textAlign: "right", fontWeight: 800, fontSize: 13, color: S.green }}>
                                                {(summary.totalDebit || 0).toFixed(3)}
                                            </td>
                                            <td style={{ padding: "9px 12px", textAlign: "right", fontWeight: 800, fontSize: 13, color: S.red }}>
                                                {(summary.totalCredit || 0).toFixed(3)}
                                            </td>
                                            <td style={{ padding: "9px 12px", textAlign: "right", fontWeight: 800, fontSize: 13, color: periodNetSign === "Dr" ? S.green : S.red }}>
                                                {(summary.absPeriodNetBalance || 0).toFixed(3)}
                                            </td>
                                            <td style={{ padding: "9px 12px", textAlign: "center" }}>
                                                <DrCrBadge value={periodNetSign} />
                                            </td>
                                        </tr>

                                        {/* Pagination row */}
                                        <tr style={{ background: S.bg, borderTop: `1px solid ${S.border}` }}>
                                            <td colSpan={8} style={{ padding: "8px 12px" }}>
                                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                    <div style={{ fontSize: 11, color: S.inkLight }}>
                                                        Showing {ledger.length} of {pagination.totalCount || 0} transactions
                                                    </div>
                                                    <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                                                        <button onClick={goFirst} disabled={pagination?.currentPage === 1} style={{ padding: "4px 8px", border: `1px solid ${S.border}`, borderRadius: S.radiusSm, background: S.bg, color: pagination?.currentPage === 1 ? S.inkFaint : S.inkMid, cursor: pagination?.currentPage === 1 ? "not-allowed" : "pointer", fontSize: 11 }}>
                                                            <DoubleLeftOutlined />
                                                        </button>
                                                        <button onClick={goPrev} disabled={pagination?.currentPage === 1} style={{ padding: "4px 8px", border: `1px solid ${S.border}`, borderRadius: S.radiusSm, background: S.bg, color: pagination?.currentPage === 1 ? S.inkFaint : S.inkMid, cursor: pagination?.currentPage === 1 ? "not-allowed" : "pointer", fontSize: 11 }}>
                                                            <LeftOutlined />
                                                        </button>
                                                        {pageNumbers().map((pg, i) => (
                                                            <button key={i} onClick={() => typeof pg === "number" && goTo(pg)} disabled={pg === "..."} style={{
                                                                padding: "4px 9px", border: `1px solid ${S.border}`, borderRadius: S.radiusSm, fontSize: 11,
                                                                cursor: pg === "..." ? "default" : "pointer",
                                                                background: pg === pagination.currentPage ? S.ink : S.bg,
                                                                color: pg === pagination.currentPage ? "#fff" : S.inkMid,
                                                                fontWeight: pg === pagination.currentPage ? 700 : 400,
                                                            }}>
                                                                {pg}
                                                            </button>
                                                        ))}
                                                        <button onClick={goNext} disabled={pagination?.currentPage === pagination?.totalPages} style={{ padding: "4px 8px", border: `1px solid ${S.border}`, borderRadius: S.radiusSm, background: S.bg, color: pagination?.currentPage === pagination?.totalPages ? S.inkFaint : S.inkMid, cursor: pagination?.currentPage === pagination?.totalPages ? "not-allowed" : "pointer", fontSize: 11 }}>
                                                            <RightOutlined />
                                                        </button>
                                                        <button onClick={goLast} disabled={pagination?.currentPage === pagination?.totalPages} style={{ padding: "4px 8px", border: `1px solid ${S.border}`, borderRadius: S.radiusSm, background: S.bg, color: pagination?.currentPage === pagination?.totalPages ? S.inkFaint : S.inkMid, cursor: pagination?.currentPage === pagination?.totalPages ? "not-allowed" : "pointer", fontSize: 11 }}>
                                                            <DoubleRightOutlined />
                                                        </button>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Empty state */}
                    {noData && (
                        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 20px", background: S.bg, borderRadius: S.radius, border: `1px dashed ${S.border}` }}>
                            <SwapOutlined style={{ fontSize: 28, color: S.inkFaint, marginBottom: 14 }} />
                            <div style={{ fontSize: 15, fontWeight: 600, color: S.inkMid, marginBottom: 6 }}>
                                {filters.supplier_id ? "No transactions found" : "Select a trading partner"}
                            </div>
                            <div style={{ fontSize: 13, color: S.inkFaint, textAlign: "center", maxWidth: 360, lineHeight: 1.6 }}>
                                {filters.supplier_id
                                    ? "No transactions found for this date range. Try widening the filter."
                                    : "Choose a supplier above. Only suppliers linked to a customer will show data here."}
                            </div>
                        </div>
                    )}
                </div>
            </Loadercomponent>
        </Header>
    );
};

export default TradingPartnerReport;